import { useEffect, useState } from "react";
import { useMessageStore } from "@/store/messages";
import { useProfileStore, getFacilityKey, getPatientKey } from "@/store/profile";
import msgRequest from "@/api/msg-request";
import { formatDateTime } from "@/utils/format";
import { SubmitButton } from "@/components/ui/SubmitButton";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";

const replySchema = z.object({
  message: z.string().min(1, "Message is required").max(8000),
});

type ReplyForm = z.infer<typeof replySchema>;

interface ThreadMessage {
  id: number;
  messageText?: string;
  fromPatient?: boolean;
  fromName?: string;
  entryDate?: string;
}

function getFromName(msg: ThreadMessage, officeName: string): string {
  return msg.fromPatient ? (msg.fromName ?? "") : officeName;
}

function getToName(msg: ThreadMessage, officeName: string): string {
  return msg.fromPatient ? officeName : (msg.fromName ?? "");
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export interface MsgDetailsProps {
  hideBack?: boolean;
  /** Conversation bubbles + inline reply; on small viewports the office/avatar top bar is hidden (parent shows mobile header). */
  inlineReply?: boolean;
}

export function MsgDetails({ hideBack, inlineReply }: MsgDetailsProps) {
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const currentMsgInfo = useMessageStore((s) => s.currentMsgInfo);
  const setMsgCurrentPage = useMessageStore((s) => s.setMsgCurrentPage);

  const [thread, setThread] = useState<ThreadMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ReplyForm>({
    resolver: zodResolver(replySchema),
    defaultValues: { message: "" },
  });

  const subject = (currentMsgInfo?.subject as string) ?? "";
  const messageId = currentMsgInfo?.id != null ? Number(currentMsgInfo.id) : 0;

  const loadThread = async () => {
    const facilityKey = getFacilityKey();
    const patientKey = getPatientKey();
    if (!facilityKey || !patientKey || !messageId) return;
    setLoading(true);
    try {
      const res = await msgRequest.request.getMessageThread(
        facilityKey,
        patientKey,
        { parentId: String(messageId) }
      );
      const list = (res?.data as ThreadMessage[]) ?? [];
      list.sort((a, b) => (a.id > b.id ? 1 : b.id > a.id ? -1 : 0));
      setThread(list);
      await msgRequest.request.updateMessageRead(facilityKey, {
        id: String(messageId),
        read: true,
      });
    } catch {
      setThread([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadThread();
  }, [currentPatient?.facilityID, currentPatient?.patientID, messageId]);

  const onReplySubmit = async (data: ReplyForm) => {
    const facilityKey = getFacilityKey();
    const patientKey = getPatientKey();
    if (!facilityKey || !patientKey || !messageId) return;
    try {
      await msgRequest.request.insertMessageReply(
        facilityKey,
        patientKey,
        { parentId: String(messageId), message: data.message }
      );
      toast.success("Reply sent");
      setReplyOpen(false);
      reset();
      loadThread();
    } catch {
      toast.error("Failed to send reply");
    }
  };

  if (!currentPatient) return null;

  const officeName = currentPatient.officeName ?? "";
  const lastIndex = thread.length - 1;
  const lastFromProvider = thread.length > 0 && !thread[lastIndex]?.fromPatient;

  if (inlineReply) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div
          className="flex flex-shrink-0 items-center gap-2.5 border-b border-healthsource-lightLavender py-3.5 pl-[clamp(0.9rem,4.5vw,1.4rem)] pr-4 max-md:hidden"
        >
          <div
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-extrabold text-white"
            style={{ backgroundColor: "#2A4064" }}
          >
            {getInitials(officeName)}
          </div>
          <p className="text-sm font-extrabold text-healthsource-dark">
            {officeName}
          </p>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-[clamp(0.75rem,4.5vw,1.4rem)] py-3.5 sm:py-4">
          {loading && (
            <p className="text-sm text-healthsource-gray">Loading thread…</p>
          )}
          {!loading && thread.length === 0 && (
            <p className="text-sm text-healthsource-gray">
              No messages in this thread.
            </p>
          )}
          {!loading && thread.length > 0 && (
            <div className="flex flex-col gap-2.5">
              {thread.map((msg) => {
                const isPatient = !!msg.fromPatient;
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isPatient ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className="max-w-[min(18rem,72%)] sm:max-w-[72%] break-words rounded-xl px-3.5 py-2.5"
                      style={{
                        background: isPatient
                          ? "#2A4064"
                          : "rgba(42,64,100,0.12)",
                        borderRadius: isPatient
                          ? "12px 12px 2px 12px"
                          : "12px 12px 12px 2px",
                      }}
                    >
                      <p
                        className="text-[15px] leading-snug [overflow-wrap:anywhere]"
                        style={{
                          color: isPatient ? "#fff" : "#1E252B",
                        }}
                      >
                        {msg.messageText ?? ""}
                      </p>
                      <p
                        className="mt-1 text-[13px]"
                        style={{
                          color: isPatient
                            ? "rgba(255,255,255,0.8)"
                            : "#6F7375",
                          textAlign: isPatient ? "right" : "left",
                        }}
                      >
                        {msg.entryDate
                          ? formatDateTime(msg.entryDate)
                          : ""}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {!loading && lastFromProvider && (
          <form
            onSubmit={handleSubmit(onReplySubmit)}
            className="flex flex-shrink-0 items-end gap-2 border-t border-healthsource-lightLavender px-[clamp(0.65rem,3.5vw,1.1rem)] py-2.5 sm:gap-2.5 sm:px-4 sm:py-3"
          >
            <textarea
              rows={1}
              {...register("message")}
              placeholder="Type a message..."
              className="min-h-11 min-w-0 flex-1 resize-none rounded-lg border border-healthsource-lightLavender bg-white px-3.5 py-2.5 text-base leading-snug text-healthsource-dark outline-none focus:border-healthsource-navy focus:ring-2 focus:ring-healthsource-navy/20"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="min-h-11 flex-shrink-0 rounded-lg bg-healthsource-red px-4 py-2 text-sm font-bold text-white hover:opacity-90 disabled:opacity-60"
            >
              {isSubmitting ? "…" : "Send"}
            </button>
          </form>
        )}
        {errors.message?.message && (
          <p className="px-4 text-xs text-destructive">{errors.message.message}</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!hideBack && (
        <button
          type="button"
          onClick={() => setMsgCurrentPage("MsgList")}
          className="text-primary underline hover:no-underline"
        >
          ← Back to Messages
        </button>
      )}

      <h3 className="font-semibold">{subject}</h3>

      {loading && <p className="text-muted-foreground">Loading thread…</p>}
      {!loading && thread.length === 0 && (
        <p className="text-muted-foreground">No messages in this thread.</p>
      )}
      {!loading && thread.length > 0 && (
        <div className="space-y-3">
          {thread.map((msg, index) => (
            <div
              key={msg.id}
              className="rounded-lg border border-border bg-card p-4"
            >
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium">
                  {getFromName(msg, officeName)} → {getToName(msg, officeName)}
                </p>
                <span className="text-xs text-muted-foreground">
                  {msg.entryDate
                    ? formatDateTime(msg.entryDate)
                    : ""}
                </span>
              </div>
              <p className="whitespace-pre-wrap text-sm">{msg.messageText}</p>
              {index === lastIndex && !msg.fromPatient && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setReplyOpen(true)}
                    className="text-sm text-primary underline hover:no-underline"
                  >
                    Reply
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {replyOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reply-msg-title"
        >
          <div className="w-full max-w-lg rounded-lg border border-border bg-card p-4 shadow-lg">
            <h2 id="reply-msg-title" className="text-lg font-semibold">
              Re: {subject}
            </h2>
            <form
              onSubmit={handleSubmit(onReplySubmit)}
              className="mt-4 space-y-4"
            >
              <div>
                <label htmlFor="reply-body" className="block text-sm font-medium">
                  Message
                </label>
                <textarea
                  id="reply-body"
                  rows={6}
                  maxLength={8000}
                  {...register("message")}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-destructive">
                    {errors.message.message}
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setReplyOpen(false)}
                  className="rounded-md border border-input px-4 py-2 text-sm hover:bg-muted"
                >
                  Cancel
                </button>
                <SubmitButton isSubmitting={isSubmitting}>Send</SubmitButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
